import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Square, Plus, Trash2, Timer, ChevronDown, ChevronRight, Archive, Paperclip, Download, CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { frDate, TASK_STATUSES, taskStatusOf } from "@/lib/constants";
import { toast } from "sonner";

function fmtDuration(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function fmtMin(min) {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`;
}

function fmtSlot(min) {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

function mondayISO(isoDate) {
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function TaskStatusSelect({ task, onChange, testid }) {
  return (
    <Select value={taskStatusOf(task.status).id} onValueChange={(v) => onChange(task, v)}>
      <SelectTrigger data-testid={testid} className="h-auto w-auto border-0 shadow-none p-0 focus:ring-0 [&>svg]:hidden">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border cursor-pointer whitespace-nowrap ${taskStatusOf(task.status).cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${taskStatusOf(task.status).dot}`} />
          {taskStatusOf(task.status).label}
        </span>
      </SelectTrigger>
      <SelectContent>
        {TASK_STATUSES.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`${testid}-option-${s.id}`}>{s.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function TodoCard({ tasks, onChanged, clientId = null, groupByClient = false }) {
  const [title, setTitle] = useState("");
  const [deadline, setDeadline] = useState("");
  const [estimate, setEstimate] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [showSlots, setShowSlots] = useState(false);
  const [slots, setSlots] = useState(null);
  const [closedGroups, setClosedGroups] = useState({});
  const [clients, setClients] = useState([]);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!clientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [clientId]);

  const DONE_STATUSES = ["finalise", "livre_client", "termine"];
  const activeTasks = tasks.filter((t) => !t.archived && !DONE_STATUSES.includes(t.status));
  const doneTasks = tasks.filter((t) => !t.archived && DONE_STATUSES.includes(t.status));

  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(i);
  }, []);

  const [reqDetail, setReqDetail] = useState(null);
  useEffect(() => {
    setReqDetail(null);
    setShowSlots(false);
    setSlots(null);
    const t = tasks.find((x) => x.id === detailId);
    if (t?.source_request_id) {
      api.get(`/client-requests/${t.source_request_id}/detail`)
        .then((r) => setReqDetail(r.data)).catch(() => {});
    }
  }, [detailId]);

  const elapsed = (t) => {
    let s = t.time_spent_sec || 0;
    if (t.timer_started_at) s += Math.floor((Date.now() - new Date(t.timer_started_at).getTime()) / 1000);
    return s;
  };

  const urgency = (t) => {
    if (!t.deadline || DONE_STATUSES.includes(t.status) || t.archived) return null;
    const days = Math.ceil((new Date(t.deadline + "T23:59:59").getTime() - Date.now()) / 86400000);
    if (days <= 2) return "urgent";
    if (days <= 3) return "soon";
    return null;
  };

  const add = async () => {
    if (!title.trim()) return;
    try {
      await api.post("/tasks", {
        title: title.trim(),
        client_id: clientId || null,
        deadline: deadline || null,
        estimate_min: estimate ? Number(estimate) : null,
      });
      setTitle(""); setDeadline(""); setEstimate("");
      toast.success("Tâche ajoutée");
      onChanged?.();
    } catch { toast.error("Erreur lors de l'ajout"); }
  };

  const patch = async (id, update) => {
    try { await api.patch(`/tasks/${id}`, update); onChanged?.(); }
    catch { toast.error("Erreur d'enregistrement"); }
  };

  const toggleTimer = (t) => {
    if (t.timer_started_at) {
      const addSec = Math.max(1, Math.floor((Date.now() - new Date(t.timer_started_at).getTime()) / 1000));
      patch(t.id, { timer_started_at: null, time_spent_sec: (t.time_spent_sec || 0) + addSec });
      toast.success(`Temps enregistré : ${fmtDuration((t.time_spent_sec || 0) + addSec)}`);
    } else {
      patch(t.id, { timer_started_at: new Date().toISOString() });
      toast.info("Chronomètre démarré");
    }
  };

  const loadSlots = async () => {
    if (!current) return;
    setShowSlots(true); setSlots(null);
    try {
      const { data } = await api.get(`/agenda/tasks/${current.id}/slot-suggestions`);
      setSlots(data);
    } catch { toast.error("Impossible de charger les créneaux"); setShowSlots(false); }
  };

  const attachToRecurring = async (blockId) => {
    try {
      await api.patch(`/agenda/blocks/${blockId}`, { task_id: current.id, confirm_after_deadline: true });
      toast.success("Tâche rattachée au créneau récurrent du client");
      setShowSlots(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors du rattachement"); }
  };

  const blockFreeSlot = async (s) => {
    try {
      await api.post("/agenda/blocks", {
        task_id: current.id, date: s.date, start_min: s.start_min,
        end_min: s.start_min + (slots?.estimate_min || 60),
      });
      toast.success("Créneau bloqué dans l'agenda");
      setShowSlots(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.status === 409 ? "Ce créneau vient d'être pris" : "Erreur lors du blocage"); }
  };

  const current = tasks.find((t) => t.id === detailId) || null;
  const details = current?.details || [];

  const addItem = async () => {
    if (!newItem.trim() || !current) return;
    await patch(current.id, { details: [...details, { id: `${Date.now()}`, text: newItem.trim(), done: false }] });
    setNewItem("");
  };
  const toggleItem = (item) =>
    patch(current.id, { details: details.map((d) => (d.id === item.id ? { ...d, done: !d.done } : d)) });
  const removeItem = (item) =>
    patch(current.id, { details: details.filter((d) => d.id !== item.id) });

  const renderTaskRow = (t, isDone = false) => (
    <div key={t.id} className={`flex items-center gap-2.5 px-5 py-3 border-b border-neutral-100 last:border-0 ${isDone ? "opacity-70" : ""}`} data-testid={`dash-task-${t.id}`}>
      {!isDone && (
        <button
          data-testid={`dash-task-timer-${t.id}`}
          onClick={() => toggleTimer(t)}
          title={t.timer_started_at ? "Arrêter le chronomètre" : "Démarrer le chronomètre"}
          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${
            t.timer_started_at ? "bg-red-100 text-red-600 hover:bg-red-200" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"}`}
        >
          {t.timer_started_at ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
        </button>
      )}
      <button className="flex-1 min-w-0 text-left" onClick={() => setDetailId(t.id)} data-testid={`dash-task-open-${t.id}`}>
        <p className={`text-sm truncate hover:underline ${isDone ? "line-through text-neutral-400" : ""}`}>
          {!clientId && t.client_id && (() => {
            const c = clients.find((x) => x.id === t.client_id);
            return c ? (
              <span className="inline-block align-middle text-[10px] font-semibold rounded-full px-1.5 py-0.5 mr-1.5 whitespace-nowrap"
                style={{ background: `${c.color}1A`, color: c.color }} data-testid={`task-client-${t.id}`}>
                {c.name}
              </span>
            ) : null;
          })()}
          {t.title}
        </p>
        <p className="text-[11px] text-neutral-400 flex items-center gap-1.5 flex-wrap">
          {t.deadline && (
            <span className={urgency(t) === "urgent" ? "text-red-600 font-semibold" : urgency(t) === "soon" ? "text-orange-600 font-semibold" : ""}>
              {frDate(t.deadline)}
            </span>
          )}
          {urgency(t) === "urgent" && (
            <span className="text-[9px] font-bold bg-red-100 text-red-700 border border-red-200 rounded-full px-1.5 py-0.5 tracking-wide" data-testid={`task-urgent-${t.id}`}>
              URGENT
            </span>
          )}
          {t.estimate_min ? `~${t.estimate_min} min` : ""}
          {elapsed(t) > 0 ? ` · ⏱ ${fmtDuration(elapsed(t))}` : ""}
        </p>
      </button>
      <TaskStatusSelect task={t} onChange={(task, v) => patch(task.id, { status: v })} testid={`dash-task-status-${t.id}`} />
      <button data-testid={`dash-task-archive-${t.id}`}
        onClick={async () => { await patch(t.id, { archived: true }); toast.success("Tâche archivée — retrouvez-la dans « Archives » en bas du dashboard"); }}
        title="Archiver la tâche"
        className="text-neutral-300 hover:text-neutral-700 transition-colors shrink-0">
        <Archive className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden animate-fade-up" data-testid="dashboard-todo">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
        <h3 className="font-heading font-bold">To-do</h3>
        <Link to="/taches" className="text-xs text-neutral-500 hover:text-neutral-900">Tout voir</Link>
      </div>

      <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50/50 space-y-2">
        <div className="flex gap-2">
          <input
            data-testid="dash-todo-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Ajouter une tâche… (Entrée pour valider)"
            className="flex-1 text-sm border border-neutral-200 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
          />
          <button data-testid="dash-todo-add" onClick={add}
            className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-md px-3 py-1.5 text-sm font-medium transition-colors">
            + Ajouter
          </button>
        </div>
        <div className="flex gap-2">
          <input type="date" data-testid="dash-todo-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)}
            title="Date limite" className="text-xs border border-neutral-200 rounded-md px-2 py-1 bg-white text-neutral-500" />
          <input type="number" min="0" data-testid="dash-todo-estimate" value={estimate} onChange={(e) => setEstimate(e.target.value)}
            placeholder="Temps estimé (min)" title="Temps estimé en minutes"
            className="w-36 text-xs border border-neutral-200 rounded-md px-2 py-1 bg-white text-neutral-500" />
        </div>
      </div>

      {activeTasks.length === 0 && <p className="px-5 py-6 text-sm text-neutral-400">Aucune tâche en cours. Ajoutez-en une ci-dessus.</p>}
      {groupByClient && !clientId ? (() => {
        const groups = {};
        activeTasks.forEach((t) => {
          const key = t.client_id || t.group_label || "sans-client";
          (groups[key] = groups[key] || []).push(t);
        });
        const groupName = (key) => clients.find((c) => c.id === key)?.name || (key !== "sans-client" ? key : null) || "zzz";
        const entries = Object.entries(groups).sort(([a], [b]) => groupName(a).localeCompare(groupName(b)));
        return entries.map(([cid, list]) => {
          const c = clients.find((x) => x.id === cid);
          const label = c?.name || (cid !== "sans-client" ? cid : "Sans client");
          const isClosed = closedGroups[cid] ?? true;
          return (
            <div key={cid} data-testid={`todo-group-${cid}`}>
              <button data-testid={`todo-group-toggle-${cid}`}
                onClick={() => setClosedGroups((s) => ({ ...s, [cid]: !(s[cid] ?? true) }))}
                className="w-full flex items-center gap-2 px-4 py-2 border-b border-neutral-100 bg-neutral-50/70 hover:bg-neutral-100 text-left transition-colors">
                {isClosed ? <ChevronRight className="w-3.5 h-3.5 text-neutral-400" /> : <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />}
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c?.color || "#a3a3a3" }} />
                <span className="text-xs font-semibold">{label}</span>
                <span className="text-[10px] text-neutral-400 bg-white border border-neutral-200 rounded-full px-1.5" data-testid={`todo-group-count-${cid}`}>{list.length}</span>
              </button>
              {!isClosed && list.map((t) => renderTaskRow(t))}
            </div>
          );
        });
      })() : activeTasks.map((t) => renderTaskRow(t))}

      {doneTasks.length > 0 && (
        <div className="border-t border-neutral-100" data-testid="todo-done-section">
          <button data-testid="todo-done-toggle" onClick={() => setShowDone(!showDone)}
            className="w-full flex items-center gap-2 px-5 py-3 text-left hover:bg-neutral-50 transition-colors">
            {showDone ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Tâches terminées</span>
            <span className="ml-auto text-xs text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5" data-testid="todo-done-count">{doneTasks.length}</span>
          </button>
          {showDone && doneTasks.map((t) => renderTaskRow(t, true))}
        </div>
      )}

      <Dialog open={!!current} onOpenChange={(v) => !v && setDetailId(null)}>
        <DialogContent className="max-w-md" data-testid="task-detail-modal" aria-describedby={undefined}>
          {current && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading pr-6">{current.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <TaskStatusSelect task={current} onChange={(task, v) => patch(task.id, { status: v })} testid="task-detail-status" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Date limite</p>
                    <input type="date" data-testid="task-detail-deadline" value={current.deadline || ""}
                      onChange={(e) => patch(current.id, { deadline: e.target.value || null })}
                      className="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Temps estimé (min)</p>
                    <input type="number" min="0" data-testid="task-detail-estimate" key={current.id + (current.estimate_min ?? "")}
                      defaultValue={current.estimate_min ?? ""}
                      onBlur={(e) => patch(current.id, { estimate_min: e.target.value ? Number(e.target.value) : null })}
                      className="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white" />
                  </div>
                </div>
                {(current.planned_min > 0 || current.next_slot) && (
                  <div className="border border-indigo-100 rounded-xl px-4 py-3 bg-indigo-50/40 space-y-1.5" data-testid="task-detail-planning">
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-500">Temps planifié</span>
                      <span className="font-semibold" data-testid="task-detail-planned">{fmtMin(current.planned_min || 0)}</span>
                    </div>
                    {current.next_slot && (
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-500">Prochain créneau</span>
                        <span className="font-semibold" data-testid="task-detail-next-slot">{frDate(current.next_slot.date)} · {fmtSlot(current.next_slot.start_min)}</span>
                      </div>
                    )}
                    <Link to={current.next_slot ? `/agenda?week=${mondayISO(current.next_slot.date)}&highlight=${current.next_slot.id || ""}` : "/agenda"} className="text-xs text-indigo-700 hover:underline inline-block" data-testid="task-detail-see-slots">Voir les créneaux →</Link>
                  </div>
                )}
                {current.appointment_id && (
                  <Link to={`/rdv?focus=${current.appointment_id}`} data-testid="task-open-rdv"
                    className="flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
                    <CalendarPlus className="w-3.5 h-3.5" /> Ouvrir le RDV (insérer le compte rendu)
                  </Link>
                )}
                <div className="border border-neutral-200 rounded-xl px-4 py-3 space-y-2.5" data-testid="task-schedule">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <CalendarPlus className="w-3.5 h-3.5" /> Agenda
                    </p>
                    {!showSlots && (
                      <button data-testid="task-schedule-btn" onClick={loadSlots}
                        className="text-xs font-medium bg-neutral-900 text-white rounded-md px-2.5 py-1.5 hover:bg-neutral-800 transition-colors">
                        Bloquer dans l'agenda
                      </button>
                    )}
                  </div>
                  {showSlots && !slots && <p className="text-xs text-neutral-400">Chargement des créneaux…</p>}
                  {showSlots && slots && (
                    <div className="space-y-2.5" data-testid="task-schedule-slots">
                      {slots.recurring.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-indigo-600 mb-1">
                            Temps récurrent {clients.find((c) => c.id === current.client_id)?.name || "client"} — proposé en priorité
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {slots.recurring.map((s) => (
                              <button key={s.block_id} data-testid={`task-slot-recurring-${s.block_id}`} onClick={() => attachToRecurring(s.block_id)}
                                className="text-xs border border-indigo-200 bg-indigo-50 text-indigo-800 rounded-md px-2 py-1.5 hover:bg-indigo-100 transition-colors">
                                {frDate(s.date)} · {fmtSlot(s.start_min)}–{fmtSlot(s.end_min)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {slots.free.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-neutral-500 mb-1">Créneaux disponibles (~{fmtMin(slots.estimate_min)})</p>
                          <div className="flex flex-wrap gap-1.5">
                            {slots.free.map((s) => (
                              <button key={`${s.date}-${s.start_min}`} data-testid={`task-slot-free-${s.date}-${s.start_min}`} onClick={() => blockFreeSlot(s)}
                                className="text-xs border border-neutral-200 rounded-md px-2 py-1.5 hover:bg-neutral-100 transition-colors">
                                {frDate(s.date)} · {fmtSlot(s.start_min)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {slots.recurring.length === 0 && slots.free.length === 0 && (
                        <p className="text-xs text-neutral-400" data-testid="task-schedule-empty">Aucun créneau disponible cette semaine.</p>
                      )}
                      <button onClick={() => setShowSlots(false)} className="text-[11px] text-neutral-400 hover:text-neutral-600 transition-colors">Fermer</button>
                    </div>
                  )}
                </div>
                {reqDetail?.request && (
                  <div className="border border-neutral-200 rounded-xl px-4 py-3 space-y-2" data-testid="task-detail-request">
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Demande associée</p>
                    {reqDetail.request.explanation && (
                      <p className="text-sm text-neutral-600 whitespace-pre-line" data-testid="task-detail-request-desc">{reqDetail.request.explanation}</p>
                    )}
                    {reqDetail.request.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2" data-testid="task-detail-attachments">
                        {reqDetail.request.attachments.map((a, i) => {
                          const nm = a.name || "";
                          const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(nm);
                          const isVid = /\.(mp4|mov|webm)$/i.test(nm);
                          if (isImg) {
                            return (
                              <span key={i} className="relative" data-testid={`task-att-${i}`}>
                                <a href={a.url} target="_blank" rel="noreferrer" title="Prévisualiser">
                                  <img src={a.url} alt={nm} className="w-16 h-16 object-cover rounded-lg border border-neutral-200 hover:opacity-80 transition-opacity" />
                                </a>
                                <a href={a.url} download={nm} title="Télécharger"
                                  className="absolute bottom-1 right-1 bg-white/90 rounded p-0.5 text-neutral-600 hover:text-neutral-900">
                                  <Download className="w-3 h-3" />
                                </a>
                              </span>
                            );
                          }
                          if (isVid) {
                            return (
                              <span key={i} className="relative" data-testid={`task-att-${i}`}>
                                <video src={a.url} controls className="w-28 rounded-lg border border-neutral-200" />
                                <a href={a.url} download={nm} title="Télécharger"
                                  className="absolute bottom-1 right-1 bg-white/90 rounded p-0.5 text-neutral-600 hover:text-neutral-900">
                                  <Download className="w-3 h-3" />
                                </a>
                              </span>
                            );
                          }
                          return (
                            <span key={i} className="inline-flex items-center text-[10px] bg-neutral-100 rounded overflow-hidden" data-testid={`task-att-${i}`}>
                              <a href={a.url} target="_blank" rel="noreferrer" className="px-1.5 py-1 hover:bg-neutral-200 flex items-center gap-1 transition-colors">
                                <Paperclip className="w-3 h-3" />{nm}
                              </a>
                              <a href={a.url} download={nm} title="Télécharger" className="px-1.5 py-1 border-l border-neutral-200 hover:bg-neutral-200 transition-colors">
                                <Download className="w-3 h-3" />
                              </a>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between border border-neutral-200 rounded-xl px-4 py-3 bg-neutral-50/60">
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-neutral-400" />
                    <span className="text-xs text-neutral-500">Temps passé</span>
                  </div>
                  <span className="font-heading font-bold text-lg tabular-nums" data-testid="task-detail-time">{fmtDuration(elapsed(current))}</span>
                  <button data-testid="task-detail-timer" onClick={() => toggleTimer(current)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      current.timer_started_at ? "bg-red-600 hover:bg-red-700 text-white" : "bg-neutral-900 hover:bg-neutral-800 text-white"}`}>
                    {current.timer_started_at ? <><Square className="w-3 h-3" /> Arrêter</> : <><Play className="w-3 h-3" /> Démarrer</>}
                  </button>
                </div>
                <div>
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Éléments à faire</p>
                  <div className="space-y-1.5 mb-3">
                    {details.length === 0 && <p className="text-sm text-neutral-400" data-testid="task-detail-empty">Aucun élément. Décomposez la tâche ci-dessous.</p>}
                    {details.map((item) => (
                      <div key={item.id} className="flex items-center gap-2.5 group" data-testid={`task-item-${item.id}`}>
                        <Checkbox checked={item.done} onCheckedChange={() => toggleItem(item)} data-testid={`task-item-check-${item.id}`} />
                        <span className={`text-sm flex-1 ${item.done ? "line-through text-neutral-400" : ""}`}>{item.text}</span>
                        <button onClick={() => removeItem(item)} data-testid={`task-item-delete-${item.id}`}
                          className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input data-testid="task-item-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addItem()}
                      placeholder="Ajouter un élément…"
                      className="flex-1 text-sm border border-neutral-200 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-neutral-400" />
                    <button data-testid="task-item-add" onClick={addItem}
                      className="border border-neutral-200 rounded-md px-2.5 hover:bg-neutral-50 transition-colors">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
